import { LIST_PRESETS, type TuningPreset } from '@engine/scale';

interface Props {
  activeKey: string | null;
  onPick: (key: string) => void;
}

/**
 * Horizontally-scrollable row of preset cards. Each card shows the
 * scale's name plus a MINI RULER with coloured dots at the intervals
 * so the player can recognize the shape of the pattern without knowing
 * what "Phrygian" or "Octatonic" means.
 */
export function PresetGrid({ activeKey, onPick }: Props) {
  return (
    <div className="preset-grid">
      {LIST_PRESETS().map((p) => (
        <PresetCard
          key={p.key}
          preset={p}
          active={p.key === activeKey}
          onPick={() => onPick(p.key)}
        />
      ))}
    </div>
  );
}

function PresetCard({
  preset,
  active,
  onPick,
}: {
  preset: TuningPreset;
  active: boolean;
  onPick: () => void;
}) {
  return (
    <button
      type="button"
      className={'preset-card preset-card--compact' + (active ? ' preset-card--on' : '')}
      onClick={onPick}
      title={preset.hint ?? ''}
    >
      <div className="preset-card__label">
        {preset.label}
        <span className="preset-card__badge">{preset.scaleSize}</span>
      </div>
      <MiniRuler
        scaleNotes={preset.scaleNotes}
        period={preset.scalePeriod ?? 1200}
      />
      {preset.sourceUrl && (
        <a
          className="preset-card__source"
          href={preset.sourceUrl}
          target="_blank"
          rel="noopener noreferrer"
          // Don't trigger the parent button's onPick when clicking the link.
          onClick={(e) => e.stopPropagation()}
        >
          {sourceHost(preset.sourceUrl)}
        </a>
      )}
    </button>
  );
}

/** Pull a short, recognisable label from a URL — "wikipedia.org",
 *  "xen.wiki", "huygens-fokker.org" — so the link reads naturally
 *  under the preset name. */
function sourceHost(url: string): string {
  try {
    const host = new URL(url).hostname.replace(/^www\./, '');
    // Wikipedia URLs are en.wikipedia.org / fr.wikipedia.org / ...;
    // collapse to just "wikipedia.org" for readability.
    if (host.endsWith('.wikipedia.org')) return 'wikipedia.org';
    return host;
  } catch {
    return 'source';
  }
}

function MiniRuler({
  scaleNotes,
  period,
}: {
  scaleNotes: readonly { semitones: number; cents: number }[];
  period: number;
}) {
  const hue = (i: number) => (i * 360) / Math.max(1, scaleNotes.length);
  const CX = 20;
  const CY = 20;
  const R = 14;
  const toXY = (cents: number) => {
    const a = (cents / period) * 2 * Math.PI - Math.PI / 2;
    return { x: CX + R * Math.cos(a), y: CY + R * Math.sin(a) };
  };
  return (
    <svg viewBox="0 0 40 40" className="preset-card__ruler">
      <circle cx={CX} cy={CY} r={R} fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth={1.2} />
      {(() => {
        // Same P5 detection as the main ScaleCircle — nearest degree
        // to 700¢, within 100¢.
        let p5Idx = -1;
        let bestDelta = Infinity;
        for (let d = 1; d < scaleNotes.length; d++) {
          const n = scaleNotes[d]!;
          const total = n.semitones * 100 + n.cents;
          const delta = Math.abs(total - 700);
          if (delta < bestDelta) {
            bestDelta = delta;
            p5Idx = d;
          }
        }
        if (bestDelta > 100) p5Idx = -1;
        return scaleNotes.map((n, i) => {
          const cents = n.semitones * 100 + n.cents;
          const { x, y } = toXY(cents);
          const isRoot = i === 0;
          const isP5 = i === p5Idx;
          return (
            <circle
              key={i}
              cx={x}
              cy={y}
              r={isRoot ? 3.2 : isP5 ? 2.8 : 2.4}
              fill={
                isRoot
                  ? 'var(--key-root)'
                  : isP5
                    ? 'var(--key-fifth)'
                    : `hsl(${hue(i)}, 68%, 60%)`
              }
              stroke="rgba(0,0,0,0.4)"
              strokeWidth={0.4}
            />
          );
        });
      })()}
    </svg>
  );
}
